// The agent onboarding flow: its URL, its steps and which one to show.
// Kept free of components so the route guards can import it without the page.

export const ONBOARDING_PATH = '/agent/onboarding';

/** Step keys in flow order (services/actions/onboarding.py STEP_KEYS). */
export const STEP_ORDER = ['mls', 'identity', 'email_calendar', 'social', 'brand', 'branding'];

/** The closing view after agent.onboarding.finish (?step=ready); not a step of its own. */
export const READY_VIEW = 'ready';

export const stepPath = (key) => `${ONBOARDING_PATH}?step=${key}`;

export const findStep = (onboarding, key) => (onboarding?.steps || []).find((step) => step.key === key) || null;

/** The Agent ID step must be done before the flow can finish. */
export const isAgentIdDone = (onboarding) => findStep(onboarding, 'mls')?.status === 'done';

// ---- ID check -------------------------------------------------------------------------

/** ID check states that are still moving: the page polls agent.identity.status while one holds. */
export const IDENTITY_PENDING = new Set(['in_progress', 'pending_review']);

// Didit reports a few more states than the contract names; they fold into the five the UI knows.
const IDENTITY_ALIASES = {
  awaiting_user: 'in_progress',
  resubmitted: 'in_progress',
  in_review: 'pending_review',
  expired: 'not_started',
  abandoned: 'not_started',
};
const IDENTITY_STATES = new Set(['not_started', 'in_progress', 'pending_review', 'approved', 'declined']);

/** not_started | in_progress | pending_review | approved | declined, whatever Didit called it. */
export const normalizeIdentityStatus = (value) => {
  const key = String(value || '').toLowerCase();
  const status = IDENTITY_ALIASES[key] || key;
  return IDENTITY_STATES.has(status) ? status : 'not_started';
};

/** The ID check from agent.onboarding.get: { status, required, verified_at }. */
export const identityOf = (onboarding) => {
  const identity = onboarding?.identity || {};
  const step = findStep(onboarding, 'identity');
  return {
    status: normalizeIdentityStatus(identity.status),
    required: typeof identity.required === 'boolean' ? identity.required : Boolean(step?.required ?? !onboarding?.invited),
    verifiedAt: identity.verified_at || null,
    waived: Boolean(identity.waived),
  };
};

/**
 * The one-word state the journey shows for a step, as a key of page.states in the
 * content file: done, review, started, retry, required, optional, skipped, soon or
 * reconnect. The ID check reads its own status, which polling keeps current.
 */
export const stepStateKey = (step, onboarding) => {
  if (!step) return 'optional';
  if (step.key === 'identity') {
    const { status, required } = identityOf(onboarding);
    if (status === 'approved' || step.status === 'done') return 'done';
    if (status === 'pending_review') return 'review';
    if (status === 'in_progress') return 'started';
    if (status === 'declined') return 'retry';
    if (step.status === 'skipped') return 'skipped';
    return required ? 'required' : 'optional';
  }
  switch (step.status) {
    case 'done':
      return 'done';
    case 'pending':
      return step.key === 'email_calendar' ? 'reconnect' : 'review';
    case 'skipped':
      return 'skipped';
    case 'unavailable':
      return 'soon';
    default:
      return step.required ? 'required' : 'optional';
  }
};

/**
 * Which view to show: the deep-linked step when it exists, else step 1 until the
 * Agent ID is done, else the closing view when it was asked for and the flow is
 * finished, else the first step still open, else the closing view for a finished
 * flow or the last step (so an unfinished flow can be finished).
 */
export const resolveActiveStep = (requested, onboarding) => {
  const keys = (onboarding?.steps || []).map((step) => step.key);
  if (!keys.length) return null;
  if (requested && keys.includes(requested)) return requested;
  if (!isAgentIdDone(onboarding) && keys.includes('mls')) return 'mls';
  if (requested === READY_VIEW && onboarding.flow_finished) return READY_VIEW;
  if (onboarding.next_step && keys.includes(onboarding.next_step)) return onboarding.next_step;
  return onboarding.flow_finished ? READY_VIEW : keys[keys.length - 1];
};

/** "Step {current} of {total}" style templates from the content file. */
export const formatCopy = (template, values = {}) =>
  String(template || '').replace(/\{(\w+)\}/g, (match, name) => (name in values ? String(values[name]) : match));
